import useApiError from "@hooks/useApiError";
import useApiHook from "@hooks/useApiHook";
import React, { useCallback, useEffect, useState } from "react";
import TransparentLoader from "@components/Loaders/TransparentLoader";
import modalBorder from "@assets/images/borders/modalBorder.webp";
import {
  checkIfContainsSpecialCharacter,
  containsLowerCase,
  containsNumber,
  containsUpperCase,
  isLengthGreaterThanOrEqualToEight,
} from "@src/utils/PasswordHelpers";
import checkbox from "@assets/images/mdi_check-bold.webp";
import useNotification from "@src/hooks/useNotification";
import { useNavigate } from "react-router-dom";
import type { INewPasswordForm } from "@src/types/AppTypes";

const NewPasswordForm = React.forwardRef<HTMLDivElement, INewPasswordForm>(
  ({ setModal, token, modal }, ref) => {
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState<string>("");
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [passwordDropdown, setPasswordDropdown] = useState<boolean>(false);
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [showConfirmPassword, setShowConfirmPassword] =
      useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [, setHasEightChars] = useState<boolean>(false);
    const [, setHasSpecialChar] = useState<boolean>(false);
    const [, setHasLowerAndUpperChar] = useState<boolean>(false);
    const [, setHasNumber] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const setApiError = useApiError();
    const updatePasswordEndpoint = useApiHook({
      method: "post",
      url: "/users/passwords/reset",
    });
    const flashNotification = useNotification();

    const submit = useCallback(
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
          if (
            newPassword !== confirmPassword ||
            newPassword.length > confirmPassword.length ||
            newPassword.length < confirmPassword.length
          ) {
            setError("Passwords do not match");
            return;
          }
          setIsLoading(true);
          const form = new FormData(e.target as HTMLFormElement);
          const password = form.get("password");
          const confirmNewPassword = form.get("newPassword");
          const res = await updatePasswordEndpoint<{
            status: boolean;
            message: string;
          }>({
            user: {
              password,
              password_confirmation: confirmNewPassword,
              reset_password_token: token,
            },
          });
          if (res?.data.status) {
            flashNotification(res.data);
            setModal(false);
            navigate(location.pathname, {
              replace: true,
              state: { showUpdatePasswordForm: false },
            });
          }
          if (res && res.data.status === false) {
            flashNotification(res.data);
          }
        } catch (e) {
          setApiError(e);
        } finally {
          setIsLoading(false);
        }
      },
      [
        setApiError,
        updatePasswordEndpoint,
        token,
        setModal,
        flashNotification,
        navigate,
        newPassword,
        confirmPassword,
      ]
    );

    const checkIfPasswordIsValid = useCallback((newPassword: string) => {
      const disableInput =
        containsNumber(newPassword) &&
        containsUpperCase(newPassword) &&
        containsLowerCase(newPassword) &&
        isLengthGreaterThanOrEqualToEight(newPassword) &&
        checkIfContainsSpecialCharacter(newPassword);
      return disableInput;
    }, []);

    useEffect(() => {
      if (newPassword.length) {
        setHasNumber(containsNumber(newPassword));
        setHasLowerAndUpperChar(containsUpperCase(newPassword));
        setHasLowerAndUpperChar(containsLowerCase(newPassword));
        setHasEightChars(isLengthGreaterThanOrEqualToEight(newPassword));
        setHasSpecialChar(checkIfContainsSpecialCharacter(newPassword));
      }
    }, [newPassword]);

    useEffect(() => {
      const header = document.getElementById("header");
      const footer = document.getElementById("footer");

      document.body.style.overflow = "hidden";

      if (header) {
        header.style.zIndex = "0";
      }
      if (footer) {
        footer.style.zIndex = "-1";
      }

      return () => {
        // Enable background scroll
        document.body.style.overflow = "auto";

        if (header) {
          header.style.zIndex = "";
        }
        if (footer) {
          footer.style.zIndex = "";
        }
      };
    }, []);

    useEffect(() => {
      const timeout = setTimeout(() => {
        if (error) setError("");
      }, 3000);

      return () => {
        clearTimeout(timeout);
      };
    }, [error]);

    return (
      <>
        <div
          className={`fixed w-full h-full bg-[#00000080] top-0 left-0 grid place-items-center z-[999] ${
            modal ? "block" : "hidden"
          }`}
        ></div>
        {isLoading && <TransparentLoader />}
        <div className="fixed w-full h-full bg-[#00000080] top-0 left-0 grid place-items-center z-[999] modal-content">
          <div
            id="reset-password-form"
            onAnimationEnd={() => {
              document
                .getElementById("reset-password-form")
                ?.classList.remove("form-shake");
            }}
            ref={ref}
            className="max-w-2xl p-10 max-sm:p-5 max-md:max-w-[95%] max-md:mx-auto w-full bg-gradient-to-b from-[#1D1159] to-[#00AEEF] relative rounded-[5px_30px_40px_0px]"
          >
            <img
              src={modalBorder}
              className="absolute top-0 left-0 w-full h-full z-[0]"
              alt=""
            />
            <div className="relative z-10 max-h-[70dvh] overflow-auto">
              <button
                onClick={() => {
                  navigate(location.pathname, {
                    replace: true,
                    state: { showUpdatePasswordForm: false },
                  });
                  setModal(false);
                }}
                className="text-primary sticky top-1 right-1 text-3xl ml-auto block duration-150 hover:scale-95"
                aria-label="Close"
              >
                <i className="ri-close-circle-line"></i>
              </button>
              <h1 className="text-primary text-center uppercase text-3xl mb-4">
                Reset Password
              </h1>
              <div className="text-center mt-10 max-w-md mx-auto">
                <div className="grid grid-cols-1 gap-7 max-sm:grid-cols-1 text-start">
                  <form onSubmit={submit}>
                    <div className="mb-6 relative">
                      <input
                        onFocus={() => {
                          setPasswordDropdown(true);
                        }}
                        name="password"
                        className="bg-primary w-full text-base f-family-mochiy-pop p-[1.8rem] pr-12 rounded-[5px_27px_19px_25px] bg-[length:100%_100%] bg-no-repeat bg-[url('@assets/images/borders/inputBorder.webp')]"
                        type={showNewPassword ? "text" : "password"}
                        placeholder="New password"
                        value={newPassword}
                        onBlur={() => {
                          setPasswordDropdown(false);
                        }}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <i
                        onClick={() => {
                          setShowNewPassword(!showNewPassword);
                        }}
                        className={`hover:cursor-pointer absolute right-5 top-2/4 -translate-y-2/4 ${
                          showNewPassword ? "ri-eye-off-fill" : "ri-eye-fill"
                        } text-2xl`}
                      ></i>
                      {newPassword.length > 0 && passwordDropdown && (
                        <div className="absolute bg-[#1d1159] w-full p-6 z-10 rounded-[5px_27px_19px_25px] bg-[length:100%_100%] bg-no-repeat bg-[url('@assets/images/borders/video-border.webp')]">
                          <div className="flex items-center justify-between gap-3 mb-1">
                            <span className="text-xl text-primary-gradient">
                              8 Characters
                            </span>
                            {isLengthGreaterThanOrEqualToEight(newPassword) && (
                              <img src={checkbox} className="w-6 h-6" alt="" />
                            )}
                          </div>
                          <div className="flex items-center justify-between gap-3 mb-1">
                            <span className="text-xl text-primary-gradient">
                              1 Uppercase Letter
                            </span>
                            {containsUpperCase(newPassword) && (
                              <img src={checkbox} className="w-6 h-6" alt="" />
                            )}
                          </div>
                          <div className="flex items-center justify-between gap-3 mb-1">
                            <span className="text-xl text-primary-gradient">
                              1 Lowercase Letter
                            </span>
                            {containsLowerCase(newPassword) && (
                              <img src={checkbox} className="w-6 h-6" alt="" />
                            )}
                          </div>
                          <div className="flex items-center justify-between gap-3 mb-1">
                            <span className="text-xl text-primary-gradient">
                              1 number
                            </span>
                            {containsNumber(newPassword) && (
                              <img src={checkbox} className="w-6 h-6" alt="" />
                            )}
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xl text-primary-gradient">
                              1 special Character
                            </span>
                            {checkIfContainsSpecialCharacter(newPassword) && (
                              <img src={checkbox} className="w-6 h-6" alt="" />
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <input
                        name="newPassword"
                        className="bg-primary w-full text-base p-[1.8rem] pr-12 f-family-mochiy-pop rounded-[5px_27px_19px_25px] bg-[length:100%_100%] bg-no-repeat bg-[url('@assets/images/borders/inputBorder.webp')]"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <i
                        onClick={() => {
                          setShowConfirmPassword(!showConfirmPassword);
                        }}
                        className={`hover:cursor-pointer absolute right-5 top-2/4 -translate-y-2/4 ${
                          showConfirmPassword
                            ? "ri-eye-off-fill"
                            : "ri-eye-fill"
                        } text-2xl`}
                      ></i>
                    </div>
                    <p className="text-red-500 min-h-6">{error}</p>
                    <button
                      disabled={
                        !checkIfPasswordIsValid(newPassword) ||
                        !checkIfPasswordIsValid(confirmPassword)
                      }
                      className="w-full text-2xl max-sm:text-xl customized-button rounded-[5px_20px_11px_20px] px-10"
                      type="submit"
                      aria-label="Reset Password"
                    >
                      Reset Password
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
);

export default NewPasswordForm;
